import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Calendar, Clock, MapPin, Film, Users, Handshake, Sparkles, CheckCircle2, Ticket, Lock } from "lucide-react";
import { toast } from "sonner";

const POSTER_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_filmmakers-africa/artifacts/1a722980cd9a3458_WhatsApp%20Image%202026-10-19%20at%208.45.30%20PM.jpeg";
const POSTER_LANDSCAPE_URL = "https://customer-assets-39nsmqrw.emergentagent.net/job_filmmakers-africa/artifacts/025a359dc3f52251_Cinevoya%20Launch%20and%20Film%20Premieres.png";

const HIGHLIGHTS = [
  { icon: Film, label: "Premiere Short Films", copy: "First-look screenings of the launch slate." },
  { icon: Users, label: "Meet Filmmakers", copy: "Directors, producers and cast in the room." },
  { icon: Handshake, label: "Network & Build", copy: "Distributors, festival scouts, collaborators." },
  { icon: Sparkles, label: "Celebrate African Creativity", copy: "One evening for the culture." },
];

export default function Event() {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone_number: "", guests: 1 });
  const [submitting, setSubmitting] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    api.get("/event/status").then((r) => setStatus(r.data)).catch(() => {});
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone_number) {
      toast.error("Please fill in your name, email and phone number.");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/event/register", form);
      setConfirmed({ ...data, name: form.name, email: form.email });
      toast.success("You're on the list. Check your inbox for confirmation.");
      // Refresh the seat counter.
      const s = await api.get("/event/status");
      setStatus(s.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Registration failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const isFull = status && !status.is_open;
  const seatsProgressPct = status ? Math.min(100, Math.round((status.seats_taken / status.cap) * 100)) : 0;

  return (
    <div className="min-h-screen bg-ink grain">
      <Navbar />

      {/* HERO (mobile <768px) — poster fills the fold rounded-card style.
          Desktop (≥768px) uses the full-width landscape banner below. */}
      <div className="md:hidden relative w-full pt-24 sm:pt-28 overflow-hidden" data-testid="event-hero">
        {/* Ambient cinematic wash — desktop only. Layered radial gradients
            simulate stage lights bleeding across the ink; opacity kept low
            so the poster remains the hero. */}
        <div
          className="hidden lg:block absolute inset-0 pointer-events-none"
          data-testid="event-hero-bokeh"
          aria-hidden="true"
          style={{
            background:
              "radial-gradient(60% 50% at 20% 40%, rgba(195,106,74,0.22) 0%, rgba(195,106,74,0.0) 60%)," +
              "radial-gradient(55% 45% at 82% 55%, rgba(224,148,102,0.18) 0%, rgba(224,148,102,0.0) 65%)," +
              "radial-gradient(70% 60% at 50% 90%, rgba(212,175,55,0.10) 0%, rgba(212,175,55,0.0) 70%)",
          }}
        />
        {/* Grain overlay on top of the wash — sells the "screening room"
            texture without competing with the poster. */}
        <div className="hidden lg:block absolute inset-0 grain pointer-events-none opacity-60" aria-hidden="true" />

        <div className="relative max-w-7xl mx-auto px-6">
          <div className="relative flex items-center justify-center min-h-[70vh] lg:min-h-[80vh]">
            {/* LEFT film-reel decoration — desktop only. Pure CSS: two
                concentric conic-gradient rings for the reel body and eight
                perforation dots around the rim. Rotates slowly so it feels
                alive without stealing focus. */}
            <div
              className="hidden lg:flex absolute left-6 xl:left-10 top-1/2 -translate-y-1/2 flex-col items-center gap-8 pointer-events-none select-none"
              data-testid="event-hero-reel-left"
              aria-hidden="true"
            >
              <div className="relative w-40 h-40 xl:w-52 xl:h-52 rounded-full border-2 border-terracotta/25 animate-[spin_28s_linear_infinite]" style={{ boxShadow: "0 0 60px rgba(195,106,74,0.15) inset" }}>
                <div className="absolute inset-6 rounded-full border border-terracotta/20" />
                <div className="absolute inset-14 rounded-full border border-terracotta/30 bg-terracotta/10" />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                  <span
                    key={deg}
                    className="absolute w-3 h-3 rounded-full bg-terracotta/60"
                    style={{
                      top: "50%",
                      left: "50%",
                      transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(-70px)`,
                    }}
                  />
                ))}
              </div>
              {/* Vertical film strip below the reel */}
              <div className="w-10 h-64 xl:h-96 relative overflow-hidden opacity-70">
                <div className="absolute inset-y-0 left-0 right-0 bg-black border-y border-terracotta/20" />
                <div className="absolute inset-y-0 left-0.5 w-1.5 flex flex-col justify-around">
                  {Array.from({ length: 14 }).map((_, i) => <span key={i} className="h-2.5 rounded-sm bg-terracotta/50" />)}
                </div>
                <div className="absolute inset-y-0 right-0.5 w-1.5 flex flex-col justify-around">
                  {Array.from({ length: 14 }).map((_, i) => <span key={i} className="h-2.5 rounded-sm bg-terracotta/50" />)}
                </div>
              </div>
            </div>

            {/* POSTER — natural portrait size, centred. Wrapper adds a soft
                terracotta halo so the poster reads as "lit" by the ambient
                bokeh instead of pasted on. */}
            <div className="relative z-10">
              <div
                className="hidden lg:block absolute inset-0 -m-8 rounded-3xl pointer-events-none"
                aria-hidden="true"
                style={{ boxShadow: "0 40px 120px rgba(195,106,74,0.35), 0 0 80px rgba(212,175,55,0.12)" }}
              />
              <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black">
                <img
                  src={POSTER_URL}
                  alt="Cinevoya Official Launch — 13 November 2026, Silverbird Galleria, Victoria Island Lagos"
                  className="w-full lg:w-auto lg:max-w-[420px] xl:max-w-[500px] h-auto lg:max-h-[80vh] object-contain bg-black"
                  data-testid="event-poster"
                />
              </div>
            </div>

            {/* RIGHT film-reel decoration — mirror of the left. Counter-rotates. */}
            <div
              className="hidden lg:flex absolute right-6 xl:right-10 top-1/2 -translate-y-1/2 flex-col items-center gap-8 pointer-events-none select-none"
              data-testid="event-hero-reel-right"
              aria-hidden="true"
            >
              <div className="relative w-40 h-40 xl:w-52 xl:h-52 rounded-full border-2 border-gold/25 animate-[spin_34s_linear_infinite_reverse]" style={{ boxShadow: "0 0 60px rgba(212,175,55,0.15) inset" }}>
                <div className="absolute inset-6 rounded-full border border-gold/20" />
                <div className="absolute inset-14 rounded-full border border-gold/30 bg-gold/10" />
                {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
                  <span
                    key={deg}
                    className="absolute w-3 h-3 rounded-full bg-gold/60"
                    style={{
                      top: "50%",
                      left: "50%",
                      transform: `translate(-50%, -50%) rotate(${deg}deg) translateY(-70px)`,
                    }}
                  />
                ))}
              </div>
              <div className="w-10 h-64 xl:h-96 relative overflow-hidden opacity-70">
                <div className="absolute inset-y-0 left-0 right-0 bg-black border-y border-gold/20" />
                <div className="absolute inset-y-0 left-0.5 w-1.5 flex flex-col justify-around">
                  {Array.from({ length: 14 }).map((_, i) => <span key={i} className="h-2.5 rounded-sm bg-gold/50" />)}
                </div>
                <div className="absolute inset-y-0 right-0.5 w-1.5 flex flex-col justify-around">
                  {Array.from({ length: 14 }).map((_, i) => <span key={i} className="h-2.5 rounded-sm bg-gold/50" />)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* HERO (desktop ≥768px) — full-width landscape banner. w-full h-auto
          keeps the entire image visible (no cropping) while filling the
          viewport horizontally. pt-24/sm:pt-28 clears the fixed navbar
          so the top of the artwork is never hidden behind it. */}
      <div
        className="hidden md:block relative w-full pt-24 sm:pt-28"
        data-testid="event-hero-desktop"
      >
        <img
          src={POSTER_LANDSCAPE_URL}
          alt="Cinevoya Official Launch — 13 November 2026, Silverbird Galleria, Victoria Island Lagos"
          className="block w-full h-auto"
          fetchPriority="high"
          data-testid="event-poster-desktop"
        />
      </div>

      {/* EVENT DETAILS BLOCK */}
      <div className="max-w-4xl mx-auto px-6 mt-12 sm:mt-16">
        <p className="text-xs uppercase tracking-[0.3em] text-terracotta font-semibold mb-3 flex items-center gap-2">
          <Ticket className="w-3 h-3" /> Official Launch
        </p>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-foreground" data-testid="event-title">
          You&apos;re Invited to Our Official Launch &{" "}
          <span className="text-terracotta">Short Film Premieres</span>
        </h1>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="p-5 rounded-xl bg-surface border border-white/10 flex items-start gap-3" data-testid="event-date">
            <Calendar className="w-5 h-5 text-terracotta mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-foreground/50 font-semibold">Date</p>
              <p className="font-display font-bold text-lg leading-tight mt-1">Friday<br />13 November 2026</p>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-surface border border-white/10 flex items-start gap-3" data-testid="event-time">
            <Clock className="w-5 h-5 text-terracotta mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-foreground/50 font-semibold">Time</p>
              <p className="font-display font-bold text-lg leading-tight mt-1">1:00 PM<br />Prompt</p>
              <p className="text-xs text-foreground/50 mt-1">Doors 12:30 PM</p>
            </div>
          </div>
          <div className="p-5 rounded-xl bg-surface border border-white/10 flex items-start gap-3" data-testid="event-venue">
            <MapPin className="w-5 h-5 text-terracotta mt-0.5 shrink-0" />
            <div>
              <p className="text-[10px] uppercase tracking-widest text-foreground/50 font-semibold">Venue</p>
              <p className="font-display font-bold text-base leading-tight mt-1">Silverbird Galleria Cinemas</p>
              <p className="text-xs text-foreground/60 mt-1">Victoria Island, Lagos</p>
            </div>
          </div>
        </div>
      </div>

      {/* HIGHLIGHTS */}
      <div className="max-w-6xl mx-auto px-6 mt-16 sm:mt-20" data-testid="event-highlights">
        <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-4">What to expect</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {HIGHLIGHTS.map(({ icon: Icon, label, copy }) => (
            <div key={label} className="p-5 rounded-xl bg-surface border border-white/10 hover:border-terracotta/30 transition-colors" data-testid={`highlight-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
              <div className="w-11 h-11 rounded-full bg-terracotta/15 border border-terracotta/30 flex items-center justify-center mb-3">
                <Icon className="w-5 h-5 text-terracotta" />
              </div>
              <p className="font-display font-bold text-base leading-tight mb-1">{label}</p>
              <p className="text-xs text-foreground/60 leading-relaxed">{copy}</p>
            </div>
          ))}
        </div>
      </div>

      {/* RSVP FORM */}
      <div className="max-w-2xl mx-auto px-6 mt-16 sm:mt-24 mb-20" data-testid="event-rsvp-section">
        <div className="rounded-2xl bg-surface border border-white/10 p-6 sm:p-10">
          {confirmed ? (
            <div className="text-center py-6" data-testid="event-rsvp-confirmed">
              <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="w-7 h-7 text-emerald-400" />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">You&apos;re in, {confirmed.name.split(" ")[0]}.</h2>
              <p className="text-foreground/70 leading-relaxed">
                Confirmation sent to <strong className="text-foreground/95">{confirmed.email}</strong>. Save the date — <strong className="text-terracotta">13 Nov, 1:00 PM</strong> at Silverbird VI.
              </p>
              <p className="text-xs text-foreground/50 mt-4">
                1 seat reserved · {confirmed.seats_left ?? status?.seats_left ?? 0} left overall
              </p>
            </div>
          ) : isFull ? (
            <div className="text-center py-6" data-testid="event-rsvp-full">
              <div className="w-14 h-14 rounded-full bg-red-500/15 border border-red-500/30 flex items-center justify-center mx-auto mb-4">
                <Lock className="w-7 h-7 text-red-400" />
              </div>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">Registration is closed</h2>
              <p className="text-foreground/70 leading-relaxed">
                All 200 seats have been claimed. Follow{" "}
                <a href="https://instagram.com" className="text-terracotta hover:underline">@cinevoyafrica</a>{" "}
                for any last-minute openings.
              </p>
            </div>
          ) : (
            <>
              <p className="text-xs uppercase tracking-[0.3em] text-terracotta font-semibold mb-2">RSVP</p>
              <h2 className="font-display text-2xl sm:text-3xl font-bold mb-2">Reserve your seat</h2>
              <p className="text-sm text-foreground/60 mb-6">
                Free to attend. We&apos;ll email your confirmation and a reminder the day before.
              </p>

              {status && (
                <div className="mb-6" data-testid="event-cap-indicator">
                  <div className="flex items-center justify-between text-xs mb-1.5">
                    <span className="text-foreground/60">{status.seats_taken} / {status.cap} seats claimed</span>
                    <span className="text-terracotta font-semibold">{status.seats_left} left</span>
                  </div>
                  <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-terracotta to-gold transition-all duration-700"
                      style={{ width: `${seatsProgressPct}%` }}
                    />
                  </div>
                </div>
              )}

              <form onSubmit={submit} className="space-y-4" data-testid="event-rsvp-form">
                <div>
                  <label htmlFor="rsvp-name" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Full name</label>
                  <Input
                    id="rsvp-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    placeholder="Ada Obi"
                    className="bg-ink border-white/10 mt-1.5"
                    required
                    data-testid="rsvp-name-input"
                  />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="rsvp-email" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Email</label>
                    <Input
                      id="rsvp-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="ada@example.com"
                      className="bg-ink border-white/10 mt-1.5"
                      required
                      data-testid="rsvp-email-input"
                    />
                  </div>
                  <div>
                    <label htmlFor="rsvp-phone" className="text-xs uppercase tracking-widest text-foreground/50 font-semibold">Phone</label>
                    <Input
                      id="rsvp-phone"
                      value={form.phone_number}
                      onChange={(e) => setForm({ ...form, phone_number: e.target.value })}
                      placeholder="+234 801 234 5678"
                      className="bg-ink border-white/10 mt-1.5"
                      required
                      data-testid="rsvp-phone-input"
                    />
                  </div>
                </div>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-terracotta hover:bg-ember text-white font-semibold h-11"
                  data-testid="rsvp-submit-btn"
                >
                  {submitting ? "Reserving…" : "Reserve my seat"}
                </Button>
                <p className="text-[11px] text-foreground/40 text-center">
                  By registering you agree to receive event updates from Cinevoya. We never share your details.
                </p>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
